import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  inferPhaseOfPlay,
  PHASE_OF_PLAY_META,
  type PhaseOfPlay,
} from '@4cm/core/phaseOfPlay';
import { Card, GlassPill } from '../primitives';
import { TacticsBoard2D } from '../tactics2d';
import { TacticsBrainPanel } from '../TacticsBrainPanel';
import { DrillTimeline, type TimelineDrill } from './DrillTimeline';
import { PhaseCoverageStrip } from './PhaseCoverageStrip';
import './SessionCanvas.css';

/**
 * Stable slug for a drill name — drives the per-drill persistKey
 * (`tactics-drill-${drillId}`) and the chip's thumbnail testid. We use
 * a deterministic slug (lowercased, non-alphanumerics collapsed to a
 * single hyphen, trimmed) plus an index fallback to disambiguate
 * unnamed slots.
 *
 * Reordering keeps the same drill name → same slug → its layout
 * follows the drill. Renames produce a new slot, which is the
 * intuitive behaviour for grassroots flows where drills don't carry
 * server ids until the session is saved.
 */
export function drillIdFor(name: string, index: number): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug.length > 0 ? slug : `drill-${index}`;
}

/** Local Drill shape the canvas operates on. The Brain draft payload
 *  is intentionally loose (`unknown` / record), so we parse it down to
 *  this canonical shape on every render. */
export interface DrillCard {
  name: string;
  phase: PhaseOfPlay | null;
  duration_mins: number | null;
  description?: string | null;
  coaching_points?: string[];
  primary_phase?: string | null;
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

function asNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim().length > 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function coercePhase(v: unknown): PhaseOfPlay | null {
  if (typeof v !== 'string') return null;
  const lower = v.toLowerCase();
  if (lower === 'attacking' || lower === 'defending' || lower === 'transitions' || lower === 'individual') {
    return lower;
  }
  return null;
}

/**
 * Parse a Brain session draft payload (`turn.draft.payload`) into the
 * canonical `DrillCard[]` shape. Tolerant of missing fields — Brain's
 * v1.1 prompt uses `primary_phase` for academy + `phase` for grassroots.
 * Falls back to `inferPhaseOfPlay()` over the drill name when neither
 * is set, so the phase strip still has data to draw.
 */
export function parseDrillsFromDraft(payload: unknown): DrillCard[] {
  if (!payload || typeof payload !== 'object') return [];
  const p = payload as Record<string, unknown>;
  const raw = Array.isArray(p.drills) ? p.drills : [];
  return raw.map((entry): DrillCard => {
    const d = (entry ?? {}) as Record<string, unknown>;
    const name = asString(d.name) ?? 'Untitled drill';
    const phase = coercePhase(d.phase) ?? coercePhase(d.primary_phase) ?? inferPhaseOfPlay(name);
    const duration = asNumber(d.duration_mins);
    return {
      name,
      phase,
      duration_mins: duration,
      description: asString(d.description),
      coaching_points: Array.isArray(d.coaching_points)
        ? (d.coaching_points as unknown[]).map((cp) => String(cp)).filter((s) => s.length > 0)
        : undefined,
      primary_phase: asString(d.primary_phase),
    };
  });
}

export interface SessionCanvasProps {
  /** Raw payload from `BrainTurn.draft.payload` — typed loosely on
   *  purpose so the canvas can tolerate any session-shaped Brain
   *  draft, including the offline / placeholder shapes. */
  draftPayload: unknown;
  /** Calibration toggle — at novice the canvas hides itself and the
   *  parent renders Brain only. */
  showCanvas: boolean;
  /** Click "Switch to Brain" — flips back to the Brain-only flow. */
  onSwitchToBrain?: () => void;
  /** Optional title override; defaults to the payload session_title. */
  title?: string;
  /** Optional override drills — mostly for tests / Storybook so the
   *  canvas can be exercised without a Brain payload. */
  drillsOverride?: ReadonlyArray<DrillCard>;
}

/**
 * Session Canvas (Phase C) — canvas-aware authoring view that sits
 * alongside the Brain conversation in `SessionNew`. Drill timeline
 * at the bottom, active-drill 2D board in the centre (stubbed to a
 * placeholder while Phase B's `<TacticsBoard2D>` is being built by
 * a parallel agent), inline editor in the right rail.
 *
 * Not a replacement for Brain — Brain stays the structural author.
 * The canvas reflects the latest Brain draft and lets a coach
 * select a drill, see the phase coverage strip update live, and
 * fine-tune layouts once Phase B lands.
 */
export function SessionCanvas({
  draftPayload,
  showCanvas,
  onSwitchToBrain,
  title,
  drillsOverride,
}: SessionCanvasProps) {
  // Parse incoming draft once per payload, then mirror locally so
  // re-orders inside the canvas don't immediately spring back to the
  // Brain shape. When Brain emits a new draft, we sync from it.
  const parsedFromDraft = useMemo(
    () => (drillsOverride ? Array.from(drillsOverride) : parseDrillsFromDraft(draftPayload)),
    [draftPayload, drillsOverride],
  );
  const [drills, setDrills] = useState<DrillCard[]>(parsedFromDraft);
  const [liveMessage, setLiveMessage] = useState('');

  // Drills carry no server id at this stage of the flow — slug the name
  // so the per-drill tactics persistKey is stable across re-renders.
  // Phase 5: each chip has its own `tactics-drill-${id}` slot in
  // localStorage, separate from any session-level tactics board.
  const drillIds = useMemo(
    () => drills.map((d, i) => drillIdFor(d.name, i)),
    [drills],
  );
  const resolveDrillId = useCallback<(d: TimelineDrill, i: number) => string>(
    (_d, i) => drillIds[i] ?? `drill-${i}`,
    [drillIds],
  );

  // Track which drill is selected by *id*, not index — a reorder keeps
  // the same drill highlighted instead of pointing at whatever landed
  // in that slot. `null` means nothing selected; the right-hand pane
  // shows the "Select a drill to edit its tactical layout" hint.
  const [selectionId, setSelectionId] = useState<string | null>(null);

  // Resync when the underlying Brain draft changes.
  useEffect(() => {
    setDrills(parsedFromDraft);
  }, [parsedFromDraft]);

  // Drop the selection if its drill no longer exists (removal, rename,
  // or Brain replacing the list). Index-based fallback would silently
  // point at the wrong drill; explicit clearing is safer.
  useEffect(() => {
    if (selectionId && !drillIds.includes(selectionId)) {
      setSelectionId(null);
    }
  }, [selectionId, drillIds]);

  // Append a zero-width space when the same message repeats so screen
  // readers re-announce it (aria-live skips identical strings).
  const announce = useCallback((message: string) => {
    setLiveMessage((prev) => (prev === message ? `${message}​` : message));
  }, []);

  if (!showCanvas) return null;

  const sessionTitle =
    title ??
    (draftPayload && typeof draftPayload === 'object'
      ? asString((draftPayload as Record<string, unknown>).session_title) ??
        asString((draftPayload as Record<string, unknown>).title)
      : null) ??
    'New session';

  const timelineInput: TimelineDrill[] = drills.map((d) => ({
    name: d.name,
    phase: d.phase,
    duration_mins: d.duration_mins,
  }));

  const selectedIdx = selectionId ? drillIds.indexOf(selectionId) : -1;
  const selectedDrill = selectedIdx >= 0 ? drills[selectedIdx] ?? null : null;
  const selectedPersistKey = selectionId ? `tactics-drill-${selectionId}` : null;

  const totalMins = drills.reduce((sum, d) => sum + Number(d.duration_mins ?? 0), 0);

  return (
    <section
      className="session-canvas"
      data-testid="session-canvas"
      aria-label="Session canvas — drag drills to reorder, click to select"
    >
      <header className="session-canvas__head">
        <div className="session-canvas__title-row">
          <GlassPill mono>canvas · session designer</GlassPill>
          <h2 className="session-canvas__title">{sessionTitle}</h2>
          <span className="session-canvas__meta">
            {drills.length} drill{drills.length === 1 ? '' : 's'}
            {totalMins > 0 ? ` · ${totalMins} min` : ''}
          </span>
        </div>
        {onSwitchToBrain && (
          <button
            type="button"
            className="session-canvas__switch"
            onClick={onSwitchToBrain}
            data-testid="switch-to-brain"
            aria-label="Switch to Brain-only flow"
          >
            Switch to Brain
          </button>
        )}
      </header>

      <PhaseCoverageStrip drills={drills} />

      <div className="session-canvas__split">
        <aside
          className="session-canvas__editor session-canvas__split-left"
          aria-label="Selected drill detail"
        >
          {selectedDrill ? (
            <Card variant="opaque" className="session-canvas__editor-card">
              <div className="session-canvas__editor-head">
                <span className="session-canvas__editor-eyebrow">
                  drill {selectedIdx + 1} of {drills.length}
                </span>
                <h3 className="session-canvas__editor-title">{selectedDrill.name}</h3>
                <div className="session-canvas__editor-meta">
                  {selectedDrill.phase && (
                    <span
                      className="session-canvas__editor-phase"
                      style={{ color: PHASE_OF_PLAY_META[selectedDrill.phase].colorHex }}
                    >
                      <span aria-hidden="true">{PHASE_OF_PLAY_META[selectedDrill.phase].glyph}</span>
                      {PHASE_OF_PLAY_META[selectedDrill.phase].label}
                    </span>
                  )}
                  {selectedDrill.duration_mins != null && (
                    <span className="session-canvas__editor-dur">{selectedDrill.duration_mins} min</span>
                  )}
                </div>
              </div>
              {selectedDrill.description && (
                <p className="session-canvas__editor-desc">{selectedDrill.description}</p>
              )}
              {selectedDrill.coaching_points && selectedDrill.coaching_points.length > 0 && (
                <ul className="session-canvas__editor-points">
                  {selectedDrill.coaching_points.map((cp, i) => (
                    <li key={i}>{cp}</li>
                  ))}
                </ul>
              )}
              <p className="session-canvas__editor-hint">
                Drag tokens on the pitch to refine, or ask Brain on the right to redraw the layout.
              </p>
            </Card>
          ) : (
            <Card variant="opaque" className="session-canvas__editor-card">
              <p className="session-canvas__editor-empty">
                {drills.length === 0
                  ? "Brain hasn't drafted any drills yet. Type or speak a focus and the canvas will fill in."
                  : 'Pick a drill from the timeline to see its detail here.'}
              </p>
            </Card>
          )}

          <DrillTimeline
            drills={timelineInput}
            selectedIndex={selectedIdx}
            onSelect={(i) => setSelectionId(drillIds[i] ?? null)}
            onAnnounce={announce}
            drillIdFor={resolveDrillId}
            onReorder={(next) => {
              // Reorder the canonical drill list using name+phase as the
              // identity key. Avoids relying on object reference because
              // TimelineDrill is a slimmer projection.
              const reordered: DrillCard[] = next.map((tl) => {
                const found = drills.find(
                  (d) => d.name === tl.name && d.phase === tl.phase && d.duration_mins === tl.duration_mins,
                );
                if (found) return found;
                return {
                  name: tl.name,
                  phase: tl.phase ?? null,
                  duration_mins: tl.duration_mins ?? null,
                  description: null,
                };
              });
              setDrills(reordered);
            }}
            onRemove={(i) => {
              const removedId = drillIds[i];
              setDrills((current) => current.filter((_, idx) => idx !== i));
              if (selectionId && removedId === selectionId) {
                setSelectionId(null);
              }
            }}
          />
        </aside>

        <section
          className="session-canvas__split-right"
          data-testid="session-canvas-tactics"
          aria-label="Drill tactical layout"
        >
          {selectedPersistKey ? (
            <div className="session-canvas__tactics-pane">
              <div
                className="session-canvas__tactics-board"
                data-testid="session-canvas-board"
                data-persist-key={selectedPersistKey}
              >
                <TacticsBoard2D
                  key={selectedPersistKey}
                  persistKey={selectedPersistKey}
                  homeLabel={selectedDrill?.name ?? 'Home'}
                />
              </div>
              <div className="session-canvas__tactics-brain">
                <TacticsBrainPanel
                  key={selectedPersistKey}
                  persistKey={selectedPersistKey}
                  surface="2d"
                />
              </div>
            </div>
          ) : (
            <div
              className="session-canvas__tactics-hint"
              data-testid="session-canvas-tactics-hint"
              role="note"
            >
              <strong>Select a drill</strong>
              <span>to edit its tactical layout on the pitch.</span>
            </div>
          )}
        </section>
      </div>

      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="session-canvas-live"
      >
        {liveMessage}
      </div>
    </section>
  );
}
